import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, XCircle, HelpCircle, RefreshCw, Terminal } from 'lucide-react'
import { useTranslation } from 'react-i18next'

type CheckStatus = 'ok' | 'warning' | 'error' | 'unknown'

interface DiagnosticCheck {
  key:     string
  name:    string
  status:  CheckStatus
  lastRun: string | null
  detail:  string
  error:   string | null
}

interface DiagnosticsResponse {
  hasErrors:   boolean
  hasWarnings: boolean
  checks:      DiagnosticCheck[]
  ts:          string
}

function relativeTime(iso: string | null): string {
  if (!iso) return 'never'
  const diffMs  = Date.now() - new Date(iso).getTime()
  const diffMin = diffMs / 60_000
  if (diffMin < 2)   return 'just now'
  if (diffMin < 60)  return `${Math.round(diffMin)}m ago`
  const diffH = diffMin / 60
  if (diffH < 24)    return `${Math.round(diffH)}h ago`
  return `${Math.round(diffH / 24)}d ago`
}

function StatusIcon({ status }: { status: CheckStatus }) {
  if (status === 'ok')      return <CheckCircle2  className="w-5 h-5 text-emerald-500" />
  if (status === 'warning') return <AlertTriangle className="w-5 h-5 text-amber-500" />
  if (status === 'error')   return <XCircle       className="w-5 h-5 text-red-500" />
  return <HelpCircle className="w-5 h-5 text-ink-400" />
}

function StatusBadge({ status }: { status: CheckStatus }) {
  const { t } = useTranslation()
  const cls = {
    ok:      'bg-emerald-50 text-emerald-700 border-emerald-200',
    warning: 'bg-amber-50  text-amber-700   border-amber-200',
    error:   'bg-red-50    text-red-700     border-red-200',
    unknown: 'bg-ink-50   text-ink-500    border-ink-200',
  }[status]
  const label = { ok: t('diagnostics.ok'), warning: t('diagnostics.warning'), error: t('diagnostics.error'), unknown: t('common.unknown') }[status]
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>
      {label}
    </span>
  )
}

function CheckCard({ check }: { check: DiagnosticCheck }) {
  const isSetupSession = check.key === 'fedex_scraper' &&
    (check.error ?? '').includes('--setup-session')

  return (
    <div className={`rounded-xl border p-4 flex flex-col gap-2 ${
      check.status === 'error'   ? 'border-red-200 bg-red-50/40' :
      check.status === 'warning' ? 'border-amber-200 bg-amber-50/40' :
      check.status === 'ok'      ? 'border-emerald-200 bg-white' :
                                   'border-ink-200 bg-white'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <StatusIcon status={check.status} />
          <span className="font-semibold text-[13px] text-ink-900">{check.name}</span>
        </div>
        <StatusBadge status={check.status} />
      </div>

      {/* Last run */}
      <div className="text-[11px] text-ink-500">
        Last run: <span className="font-medium text-ink-700">{relativeTime(check.lastRun)}</span>
      </div>

      {/* Detail */}
      <p className="text-[12px] text-ink-600 leading-snug">{check.detail}</p>

      {/* Error / action */}
      {check.error && (
        <div className={`rounded-lg p-3 text-[12px] leading-snug ${
          isSetupSession ? 'bg-ink-900 text-ink-100' : 'bg-red-100 text-red-800'
        }`}>
          {isSetupSession ? (
            <>
              <div className="flex items-center gap-1.5 mb-2 text-ink-400 font-medium">
                <Terminal className="w-3.5 h-3.5" />
                <span>Action required — run via X11</span>
              </div>
              <pre className="font-mono text-[11px] whitespace-pre-wrap text-emerald-300">
{`ssh -X root@finops.byelabel.com
cd /opt/byelabel
python3 scripts/scrape_fedex_invoices.py --setup-session`}
              </pre>
              <p className="mt-2 text-ink-400 text-[11px]">Log in manually in the browser that opens on your laptop. Session saves automatically.</p>
            </>
          ) : (
            check.error
          )}
        </div>
      )}
    </div>
  )
}

export function DiagnosticsView() {
  const { t } = useTranslation()
  const { data, isLoading, isFetching, refetch, dataUpdatedAt } = useQuery<DiagnosticsResponse>({
    queryKey: ['diagnostics'],
    queryFn: () => fetch('/api/diagnostics').then(r => r.json()),
    refetchInterval: 60_000,
    staleTime:       30_000,
  })

  const checks = data?.checks ?? []

  // Partition into groups
  const infra      = checks.filter(c => ['database', 'vault_share'].includes(c.key))
  const automation = checks.filter(c => ['fedex_scraper', 'gmail_watcher'].includes(c.key))
  const importers  = checks.filter(c => c.key.startsWith('importer_'))

  const errorCount   = checks.filter(c => c.status === 'error').length
  const warningCount = checks.filter(c => c.status === 'warning').length

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">{t('diagnostics.title')}</h1>
          <p className="text-sm text-ink-500 mt-1">
            {t('diagnostics.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {dataUpdatedAt > 0 && (
            <span className="text-[11px] text-ink-400">
              Updated {relativeTime(new Date(dataUpdatedAt).toISOString())}
            </span>
          )}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-ink-200 text-[12px] font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            {t('diagnostics.refresh')}
          </button>
        </div>
      </div>

      {/* Summary bar */}
      {!isLoading && (
        <div className={`rounded-xl border px-4 py-3 mb-6 flex items-center gap-3 text-sm font-medium ${
          errorCount   > 0 ? 'bg-red-50 border-red-200 text-red-700' :
          warningCount > 0 ? 'bg-amber-50 border-amber-200 text-amber-700' :
                             'bg-emerald-50 border-emerald-200 text-emerald-700'
        }`}>
          {errorCount > 0 ? (
            <><XCircle className="w-5 h-5" /> {t('diagnostics.errors_count', { count: errorCount })}</>
          ) : warningCount > 0 ? (
            <><AlertTriangle className="w-5 h-5" /> {t('diagnostics.warnings_count', { count: warningCount })}</>
          ) : (
            <><CheckCircle2 className="w-5 h-5" /> {t('diagnostics.all_healthy')}</>
          )}
        </div>
      )}

      {isLoading && (
        <div className="text-center py-20 text-ink-400 text-sm">{t('common.loading_diagnostics')}</div>
      )}

      {/* Infrastructure */}
      {infra.length > 0 && (
        <section className="mb-8">
          <h2 className="text-xs font-semibold text-ink-400 uppercase tracking-wider mb-3">{t('diagnostics.infrastructure')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {infra.map(c => <CheckCard key={c.key} check={c} />)}
          </div>
        </section>
      )}

      {/* Automations */}
      {automation.length > 0 && (
        <section className="mb-8">
          <h2 className="text-xs font-semibold text-ink-400 uppercase tracking-wider mb-3">{t('diagnostics.automations')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {automation.map(c => <CheckCard key={c.key} check={c} />)}
          </div>
        </section>
      )}

      {/* Carrier importers */}
      {importers.length > 0 && (
        <section className="mb-8">
          <h2 className="text-xs font-semibold text-ink-400 uppercase tracking-wider mb-3">{t('diagnostics.carrier_importers')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {importers.map(c => <CheckCard key={c.key} check={c} />)}
          </div>
        </section>
      )}
    </div>
  )
}
